import { useLocale } from 'shared/composables/useLocale';

/**
 * Formatting helpers shared across the Meta Campaigns module (dashboard,
 * campaign detail, and — once landed — the ad set / ad breakdown tabs).
 * Pulled out of MetaCampaignsDashboard.vue, MetaCampaignsCampaignDetail.vue
 * and CampaignTable.vue, which each had their own copy.
 */
export function useMetaCampaignsFormatters() {
  const { resolvedLocale } = useLocale();

  const brl = value =>
    new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value ?? 0);

  // ROAS below 1 means the campaign spent more than it brought in.
  // `fallbackClass` lets callers match their own neutral/no-data styling
  // (e.g. a bold summary tile vs. a muted table cell).
  const roasClass = (roas, fallbackClass = 'text-n-slate-9') => {
    if (!roas) return fallbackClass;
    return roas >= 1 ? 'text-n-teal-11' : 'text-n-ruby-11';
  };

  // Parses a `YYYY-MM-DD` string manually to avoid a UTC/local timezone
  // shift when it's handed straight to `new Date(...)`.
  const formatShortDate = dateStr => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Intl.DateTimeFormat(resolvedLocale.value, {
      day: '2-digit',
      month: 'short',
    }).format(new Date(year, month - 1, day));
  };

  const spendChartCollection = (rows, spendLabel) => ({
    labels: rows.map(row => formatShortDate(row.date)),
    datasets: [
      {
        label: spendLabel,
        backgroundColor: '#1f93ff',
        borderColor: '#1f93ff',
        data: rows.map(row => row.spend),
      },
    ],
  });

  return { brl, roasClass, formatShortDate, spendChartCollection };
}
